import axios from 'axios';
import store from '../store';
import router from '../router';
import Toast from '@/toast';

function getSubdomain() {
  return window.location.host.split('.')[0];
}

export const axiosInstance = axios.create({
  baseURL: `//${getSubdomain()}-api.${process.env.VUE_APP_API_HOST}/api/v1`,
  transformRequest: [
    function (data, headers) {
      const token = store.getters['user/token'];
      if (token != null) {
        headers['Access-token'] = token;
      }

      return JSON.stringify(data);
    }
  ],
  headers: { 'Content-Type': 'application/json' }
});

axiosInstance.interceptors.response.use(
  response => {
    return response;
  },
  error => {
    // throw error;
    if (error.response) {
      if (error.response.status === 401) {
        store.commit('user/logout');
      }

      if (error.response.status === 403) {
        Toast.show('Você não tem acesso a este recurso', 'warn');
        router.push('/', () => {});
      }
    }

    // if (error.response.status === 500) {
    //   Toast.show('Você não tem acesso a este recurso', 'warn');
    // }

    return Promise.reject(error);
  }
);

export const endpoints = {
  authenticate: '/authenticate',
  me: '/me',
  users: '/users',
  images: '/images',
  calls: '/calls',
  plans: '/plans',
  services: '/services',
  contracts: '/contracts',
  associates: '/associates',
  invoices: '/invoices',
  procedure_authorizations: '/procedure_authorizations',
  payments: '/payments',
  versions: '/versions',
  profiles: '/profiles',
  partner_categories: '/partner_categories',
  access_permissions: '/access_permissions',
  settings: '/settings',
  saae_payments_imports: '/saae_payments_imports',
  ai_chat: '/ai_chat'
};

export default {
  baseURL: axiosInstance.defaults.baseURL,
  getSubdomain: getSubdomain,
  report: params =>
    axiosInstance.get(`/r?${$.param(params)}`, {
      headers: { Accept: 'application/pdf' },
      responseType: 'blob'
    }),
  accessToken: process.env.VUE_APP_API_HOST,
  authenticate: credentials =>
    axiosInstance.post(endpoints.authenticate, credentials),
  me: () => axiosInstance.get(endpoints.me),
  images: {
    fetch: id => axiosInstance.get(`${endpoints.images}/${id}`),
    destroy: id => axiosInstance.delete(`${endpoints.images}/${id}`)
  },
  calls: {
    search: params =>
      axiosInstance.get(`${endpoints.calls}?${$.param(params)}`),
    fetch: id => axiosInstance.get(`${endpoints.calls}/${id}`),
    destroy: id => axiosInstance.delete(`${endpoints.calls}/${id}`),
    save: data => {
      const method = data.id ? 'put' : 'post';
      const url = data.id ? `${endpoints.calls}/${data.id}` : endpoints.calls;
      const options = { method: method, url: url, data: { call: data } };
      return axiosInstance(options);
    }
  },
  users: {
    transactions: id =>
      axiosInstance.get(`${endpoints.contracts}/${id}/transactions`),
    search: params =>
      axiosInstance.get(`${endpoints.users}?${$.param(params)}`),
    fetch: id => axiosInstance.get(`${endpoints.users}/${id}`),
    destroy: id => axiosInstance.delete(`${endpoints.users}/${id}`),
    save: data => {
      const method = data.id ? 'put' : 'post';
      const url = data.id ? `${endpoints.users}/${data.id}` : endpoints.users;
      const options = { method: method, url: url, data: { user: data } };
      return axiosInstance(options);
    }
  },
  payments: {
    search: params =>
      axiosInstance.get(`${endpoints.payments}?${$.param(params)}`)
  },
  plans: {
    search: params =>
      axiosInstance.get(`${endpoints.plans}?${$.param(params)}`),
    fetch: id => axiosInstance.get(`${endpoints.plans}/${id}`),
    destroy: id => axiosInstance.delete(`${endpoints.plans}/${id}`),
    save: data => {
      const method = data.id ? 'put' : 'post';
      const url = data.id ? `${endpoints.plans}/${data.id}` : endpoints.plans;
      const options = { method: method, url: url, data: { plan: data } };
      return axiosInstance(options);
    }
  },
  services: {
    search: params =>
      axiosInstance.get(`${endpoints.services}?${$.param(params)}`),
    fetch: id => axiosInstance.get(`${endpoints.services}/${id}`),
    destroy: id => axiosInstance.delete(`${endpoints.services}/${id}`),
    save: data => {
      const method = data.id ? 'put' : 'post';
      const url = data.id
        ? `${endpoints.services}/${data.id}`
        : endpoints.services;
      const options = { method: method, url: url, data: { service: data } };
      return axiosInstance(options);
    }
  },
  versions: {
    search: params =>
      axiosInstance.get(`${endpoints.versions}?${$.param(params)}`)
  },
  contracts: {
    search: params =>
      axiosInstance.get(`${endpoints.contracts}?${$.param(params)}`),
    fetch: id => axiosInstance.get(`${endpoints.contracts}/${id}`),
    enable: id => axiosInstance.post(`${endpoints.contracts}/${id}/enable`),
    audit_report: id =>
      axiosInstance.get(`${endpoints.contracts}/${id}/audit_report`),
    destroy: id => axiosInstance.delete(`${endpoints.contracts}/${id}`),
    cancel: id => axiosInstance.post(`${endpoints.contracts}/${id}/cancel`),
    save: data => {
      const method = data.id ? 'put' : 'post';
      const url = data.id
        ? `${endpoints.contracts}/${data.id}`
        : endpoints.contracts;
      const options = { method: method, url: url, data: { contract: data } };
      return axiosInstance(options);
    }
  },
  associates: {
    search: (id, params) =>
      axiosInstance.get(
        `${endpoints.contracts}/${id}${endpoints.associates}?${$.param(params)}`
      ),
    fetch: (contractId, id) =>
      axiosInstance.get(
        `${endpoints.contracts}/${contractId}/${endpoints.associates}/${id}`
      ),
    destroy: (contractId, id) =>
      axiosInstance.delete(
        `${endpoints.contracts}/${contractId}/${endpoints.associates}/${id}`
      ),
    save: (contractId, data) => {
      const method = data.id ? 'put' : 'post';
      const url = data.id
        ? `${endpoints.contracts}/${contractId}${endpoints.associates}/${data.id}`
        : `${endpoints.contracts}/${contractId}${endpoints.associates}`;
      const options = {
        method: method,
        url: url,
        data: { contract_associate: data }
      };
      return axiosInstance(options);
    }
  },
  invoices: {
    search: params =>
      axiosInstance.get(`${endpoints.invoices}?${$.param(params)}`),
    fetch: id => axiosInstance.get(`${endpoints.invoices}/${id}`),
    destroy: id => axiosInstance.delete(`${endpoints.invoices}/${id}`),
    chargeback: id => axiosInstance.post(`${endpoints.invoices}/${id}/chargeback`),
    ticket: id => axiosInstance.post(`${endpoints.invoices}/${id}/ticket`),
    requestPayment: id =>
      axiosInstance.post(`${endpoints.invoices}/${id}/request-payment`),
    save: data => {
      const method = data.id ? 'put' : 'post';
      const url = data.id
        ? `${endpoints.invoices}/${data.id}`
        : endpoints.invoices;
      const options = { method: method, url: url, data: { invoice: data } };
      return axiosInstance(options);
    }
  },
  procedureAuthorizations: {
    search: params =>
      axiosInstance.get(
        `${endpoints.procedure_authorizations}?${$.param(params)}`
      ),
    fetch: id =>
      axiosInstance.get(`${endpoints.procedure_authorizations}/${id}`),
    destroy: id =>
      axiosInstance.delete(`${endpoints.procedure_authorizations}/${id}`),
    save: data => {
      const method = data.id ? 'put' : 'post';
      const url = data.id
        ? `${endpoints.procedure_authorizations}/${data.id}`
        : endpoints.procedure_authorizations;
      const options = {
        method: method,
        url: url,
        data: { procedure_authorization: data }
      };
      return axiosInstance(options);
    }
  },
  partnerCategories: {
    fetch: id => axiosInstance.get(`${endpoints.partner_categories}/${id}`),
    destroy: id =>
      axiosInstance.delete(`${endpoints.partner_categories}/${id}`),
    save: data => {
      const method = data.id ? 'put' : 'post';
      const url = data.id
        ? `${endpoints.partner_categories}/${data.id}`
        : endpoints.partner_categories;
      const options = {
        method: method,
        url: url,
        data: { partner_category: data }
      };
      return axiosInstance(options);
    },
    search: params =>
      axiosInstance.get(`${endpoints.partner_categories}?${$.param(params)}`)
  },
  profiles: {
    search: params =>
      axiosInstance.get(`${endpoints.profiles}?${$.param(params)}`),
    fetch: id => axiosInstance.get(`${endpoints.profiles}/${id}`),
    destroy: id => axiosInstance.delete(`${endpoints.profiles}/${id}`),
    save: data => {
      const method = data.id ? 'put' : 'post';
      const url = data.id
        ? `${endpoints.profiles}/${data.id}`
        : endpoints.profiles;
      const options = { method: method, url: url, data: { profile: data } };
      return axiosInstance(options);
    }
  },
  access_permissions: () => axiosInstance.get(endpoints.access_permissions),
  settings: {
    public: () => axiosInstance.get(`${endpoints.settings}/public`),
    search: params =>
      axiosInstance.get(`${endpoints.settings}?${$.param(params)}`),
    save: data => axiosInstance.put(`${endpoints.settings}/save`, data)
  },
  aiChat: {
    send: data => axiosInstance.post(endpoints.ai_chat, data),
    downloadReport: token =>
      axiosInstance.get(`${endpoints.ai_chat}/report/${token}`, {
        responseType: 'blob'
      })
  },
  saaePaymentsImports: {
    search: () => axiosInstance.get(endpoints.saae_payments_imports),
    fetch: id => axiosInstance.get(`${endpoints.saae_payments_imports}/${id}`),
    create: file => {
      const formData = new FormData();
      formData.append('file', file);
      return axiosInstance.post(endpoints.saae_payments_imports, formData, {
        transformRequest: [
          function(data, headers) {
            const token = store.getters['user/token'];
            if (token != null) {
              headers['Access-token'] = token;
            }
            return data;
          }
        ]
      });
    }
  }
};
